/* eslint-disable camelcase */
/**
 * Multi-promo, multi-gift free-gift handler.
 *
 * Reads `window.TentBox.discounts` (an array emitted by
 * `snippets/tb-discount-config.liquid`) and, on cart changes, auto-adds
 * every gift variant for any active promo whose qualifying product is
 * in the cart. Pricing of the added lines (100% off) is handled by the
 * Shopify Discount Function deployed via the toolkit — this script
 * only handles cart manipulation.
 *
 * Selection modes (per promo `selectionMode`):
 *   - `auto` (default): every gift in a matching rule is auto-added.
 *   - `choose`: nothing is auto-added; the customer picks ONE gift via
 *     the cart drawer's `<free-gift-chooser>`, which calls
 *     `selectGift` / `swapGift`. Reconciliation (orphan/stale cleanup,
 *     qty clamp) still applies to the chosen gift once it exists.
 *
 * Behaviour preserved from the legacy single-pair handler:
 *   - sessionStorage "manually removed" tracking (per promo + gift) so
 *     a customer who deletes a gift from cart isn't fought.
 *   - Defers auto-add while the addons modal is open
 *     (`cart:added-with-modal`) and resumes on `addons-modal:closed`.
 *   - Cleans up orphaned gift lines whose qualifying product has been
 *     removed.
 *   - Updates gift quantity to match qualifying quantity for
 *     per-unit gifts (giftQty === 1).
 */
class FreeGiftHandler {
  constructor () {
    const promos = Array.isArray(window.TentBox?.discounts)
      ? window.TentBox.discounts
      : []

    this.promos = promos
      .filter((p) => p && p.active && Array.isArray(p.rules) && p.rules.length > 0)
      .map(this.normalisePromo)
      .filter(Boolean)

    if (this.promos.length === 0) return

    this.freeGiftPropertyKey = '_free_gift'
    this.promoPropertyKey = '_promo'

    this.isProcessing = false
    this.pendingRecheck = false
    this.addonsModalActive = false
    this.debugTick = 0

    // Track previous quantities to detect orphan-cleanup (only relevant
    // when a customer manually drops the qualifying product mid-session).
    this.previousQualifyingQtyByRule = new Map()
    this.previousFreeGiftQtyByGift = new Map()

    this.init()
    this.log('init', {
      promoCount: this.promos.length,
      promos: this.promos.map((p) => ({
        slug: p.slug,
        rules: p.rules.map((r) => ({
          qualifyingProductId: r.qualifyingProductId,
          gifts: r.gifts.map((g) => g.variantId),
        })),
      })),
    })
  }

  static debugEnabled () {
    try {
      if (localStorage.getItem('fg_debug') === '1') return true
      return new URLSearchParams(window.location.search).has('fg_debug')
    } catch (_e) {
      return false
    }
  }

  log (...args) {
    if (!FreeGiftHandler.debugEnabled()) return
    console.log('[free-gift]', ...args)
  }

  warn (...args) {
    if (!FreeGiftHandler.debugEnabled()) return
    console.warn('[free-gift]', ...args)
  }

  summariseLine (item) {
    if (!item) return null
    return {
      key: item.key,
      title: item.product_title || item.title,
      variant_id: item.variant_id,
      product_id: item.product_id,
      qty: item.quantity,
      final_price: item.final_price,
      original_price: item.original_price,
      _free_gift: item.properties?.[this.freeGiftPropertyKey],
      _promo: item.properties?.[this.promoPropertyKey],
    }
  }

  summariseCart (cart) {
    if (!cart) return { item_count: 0, items: [] }
    return {
      item_count: cart.item_count,
      items: (cart.items || []).map((item) => this.summariseLine(item)),
    }
  }

  debugState () {
    const manualRemoveKeys = []
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i)
        if (key && key.startsWith('freeGift_manuallyRemoved_')) {
          manualRemoveKeys.push({ key, value: sessionStorage.getItem(key) })
        }
      }
    } catch (_e) {}

    return {
      debugEnabled: FreeGiftHandler.debugEnabled(),
      isProcessing: this.isProcessing,
      pendingRecheck: this.pendingRecheck,
      addonsModalActive: this.addonsModalActive,
      debugTick: this.debugTick,
      previousQualifyingQtyByRule: Object.fromEntries(this.previousQualifyingQtyByRule),
      previousFreeGiftQtyByGift: Object.fromEntries(this.previousFreeGiftQtyByGift),
      manualRemoveKeys,
      promos: this.promos,
    }
  }

  nextTick (label) {
    this.debugTick += 1
    return `#${this.debugTick} ${label}`
  }

  /**
   * Convert the storefront payload into a normalised internal shape:
   * legacy numeric ids on every reference, plus a flat `gifts` array
   * spanning all rules for quick lookups.
   */
  normalisePromo (raw) {
    if (!raw || typeof raw !== 'object') return null
    const rules = Array.isArray(raw.rules) ? raw.rules : []
    const normRules = rules
      .map((r) => {
        const qualifyingId = FreeGiftHandler.coerceId(
          r.qualifyingProductId ?? r.qualifying_product_legacy_id ?? r.qualifying_product_id,
        )
        const gifts = Array.isArray(r.gifts) ? r.gifts : []
        const normGifts = gifts
          .map((g) => {
            const variantId = FreeGiftHandler.coerceId(
              g.giftVariantId ?? g.gift_variant_legacy_id ?? g.gift_variant_id,
            )
            const productId = FreeGiftHandler.coerceId(
              g.giftProductId ?? g.gift_product_legacy_id ?? g.gift_product_id,
            )
            const qty = Number(g.giftQty ?? g.gift_qty ?? 1)
            if (!variantId) return null
            return {
              variantId,
              productId: productId || null,
              giftQty: Number.isFinite(qty) && qty > 0 ? qty : 1,
            }
          })
          .filter(Boolean)
        if (!qualifyingId || normGifts.length === 0) return null
        return { qualifyingProductId: qualifyingId, gifts: normGifts }
      })
      .filter(Boolean)
    if (normRules.length === 0) return null
    return {
      slug: String(raw.slug || ''),
      cartLabel: raw.cartLabel || '',
      selectionMode: raw.selectionMode === 'choose' ? 'choose' : 'auto',
      rules: normRules,
    }
  }

  /**
   * Liquid's `image_url`/`product.id` filters yield numbers, but the
   * metaobject JSON encoded by the toolkit can contain either numeric
   * or GID-shaped ids. We normalise to numeric strings here because
   * Shopify's storefront cart (`/cart.js`) reports numeric ids.
   */
  static coerceId (value) {
    if (value === null || value === undefined) return null
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    if (typeof value === 'string') {
      const match = /(\d+)$/.exec(value)
      if (!match) return null
      const num = Number(match[1])
      return Number.isFinite(num) ? num : null
    }
    return null
  }

  manuallyRemovedKey (promoSlug, variantId) {
    return `freeGift_manuallyRemoved_${promoSlug}_${variantId}`
  }

  ruleKey (promoSlug, qualifyingId) {
    return `${promoSlug}::${qualifyingId}`
  }

  giftKey (promoSlug, variantId) {
    return `${promoSlug}::${variantId}`
  }

  init () {
    window.addEventListener('cart:updated', this.handleCartUpdated.bind(this))
    window.addEventListener('cart:added-with-modal', () => {
      this.addonsModalActive = true
      this.log(this.nextTick('cart:added-with-modal'), { addonsModalActive: true })
    })
    window.addEventListener('addons-modal:closed', () => {
      this.addonsModalActive = false
      this.log(this.nextTick('addons-modal:closed'), { addonsModalActive: false })
      this.checkCartState('addons-modal:closed')
    })
    this.checkCartState('init')
  }

  isManuallyRemoved (promoSlug, variantId) {
    return sessionStorage.getItem(this.manuallyRemovedKey(promoSlug, variantId)) === 'true'
  }

  setManuallyRemoved (promoSlug, variantId, value) {
    const key = this.manuallyRemovedKey(promoSlug, variantId)
    if (value) {
      sessionStorage.setItem(key, 'true')
    } else {
      sessionStorage.removeItem(key)
    }
    this.log('setManuallyRemoved', { promoSlug, variantId, value, key })
  }

  isFreeGiftLine (item, promoSlug, variantId) {
    if (!item || !item.properties) return false
    if (item.variant_id !== variantId) return false
    if (item.properties[this.freeGiftPropertyKey] !== 'true') return false
    // Older lines (pre-multi-promo) may not carry `_promo`; treat those
    // as belonging to the first matching promo so we don't double-add.
    const promo = item.properties[this.promoPropertyKey]
    return promo === undefined || promo === promoSlug
  }

  async checkCartState (source = 'checkCartState') {
    const tick = this.nextTick(`checkCartState (${source})`)
    this.log(tick, { isProcessing: this.isProcessing, pendingRecheck: this.pendingRecheck })
    try {
      const response = await fetch(`${window.routes.cart_url}.js`)
      const cart = await response.json()
      this.log(tick, 'fetched /cart.js', this.summariseCart(cart))
      this.processCart(cart, false, tick)
    } catch (err) {
      console.error('Free gift: Error checking cart state', err)
    }
  }

  handleCartUpdated (event) {
    const tick = this.nextTick('cart:updated')
    if (this.isProcessing) {
      this.pendingRecheck = true
      this.warn(tick, 'skipped — isProcessing, queued pendingRecheck', {
        detailItemCount: event.detail?.item_count,
      })
      return
    }
    if (this.addonsModalActive) {
      this.log(tick, 'skipped — addons modal active')
      return
    }

    const cart = event.detail
    if (!cart || !cart.items) {
      this.warn(tick, 'event.detail missing items, falling back to /cart.js', {
        detail: cart ? { item_count: cart.item_count, keys: Object.keys(cart) } : null,
      })
      this.checkCartState('cart:updated-missing-items')
      return
    }
    this.log(tick, 'event.detail', this.summariseCart(cart))
    this.processCart(cart, true, tick)
  }

  finishProcessing (source) {
    const wasPending = this.pendingRecheck
    this.isProcessing = false
    this.log(this.nextTick(`finishProcessing (${source})`), {
      wasPending,
      willRecheck: wasPending,
    })
    if (wasPending) {
      this.pendingRecheck = false
      this.checkCartState('pendingRecheck')
    }
  }

  /**
   * Resolve gift lines for a promo rule. Auto-added gifts carry
   * `_free_gift` regardless of whether the Discount Function has
   * priced the line yet — treating final_price > 0 as "missing"
   * caused duplicate adds, false manual-remove flags, and erroneous
   * orphan cleanup.
   */
  resolveGiftLines (cart, promoSlug, variantId) {
    const matchingGiftLines = cart.items.filter((item) =>
      this.isFreeGiftLine(item, promoSlug, variantId),
    )
    const activeGiftLine =
      matchingGiftLines.find((item) => item.final_price === 0) ||
      matchingGiftLines[0] ||
      null
    // A "ghost" line is a genuinely PAID duplicate of the gift product
    // (no `_free_gift` property at all) — NOT any line that merely isn't
    // a gift line for THIS promo. Two active promos can legitimately
    // offer the same gift variant to different qualifying products (e.g.
    // both TentBox Cargo and TentBox Classic list "Chuck Box" as a pick).
    // Checking `!isFreeGiftLine(item, promoSlug, variantId)` here used to
    // also match the OTHER promo's already-selected `_free_gift` line for
    // that same variant, so removing this promo's qualifying product
    // (e.g. deleting Cargo) would sweep up and delete a completely
    // unrelated promo's chosen gift (e.g. Classic's chosen Chuck Box).
    const ghostPaidLines = cart.items.filter(
      (item) =>
        item.variant_id === variantId &&
        item.properties?.[this.freeGiftPropertyKey] !== 'true',
    )
    const currentGiftQty = matchingGiftLines.reduce(
      (sum, item) => sum + item.quantity,
      0,
    )
    return { matchingGiftLines, activeGiftLine, ghostPaidLines, currentGiftQty }
  }

  /**
   * Sum qualifying qty × gift qty across every rule in a promo that
   * lists the same gift variant. Promos often carry multiple rules
   * (e.g. per-market product ids) for one gift — per-rule removal
   * when another rule still qualifies was deleting the gift line.
   */
  aggregateGiftRequests (promo, cart) {
    const byVariant = new Map()

    for (const rule of promo.rules) {
      const qualifyingItems = cart.items.filter(
        (item) => item.product_id === rule.qualifyingProductId,
      )
      const qualifyingQty = qualifyingItems.reduce(
        (sum, item) => sum + item.quantity,
        0,
      )

      for (const gift of rule.gifts) {
        const entry = byVariant.get(gift.variantId) || {
          variantId: gift.variantId,
          requestedQty: 0,
          rules: [],
        }
        entry.requestedQty += qualifyingQty * gift.giftQty
        entry.rules.push({
          qualifyingProductId: rule.qualifyingProductId,
          qualifyingQty,
          giftQty: gift.giftQty,
        })
        byVariant.set(gift.variantId, entry)
      }
    }

    return byVariant
  }

  /**
   * Walks every promo × rule × gift. For each rule:
   *   1. Sum the qualifying product's quantity.
   *   2. For each gift variant in the rule:
   *      a. If the qualifying qty dropped this tick AND there are
   *         paid duplicate lines (same variant, no `_free_gift`),
   *         batch-remove them.
   *      b. Otherwise reconcile the matching `_free_gift` line:
   *         - zero qualifying ⇒ remove the gift, clear manual-remove.
   *         - manual-remove flagged ⇒ leave alone.
   *         - missing ⇒ add with `giftQty * qualifyingQty`.
   *         - present but wrong qty ⇒ clamp to the correct qty.
   */
  /**
   * Gift lines carry `_promo: '<slug>'`. `this.promos` only ever contains
   * promos that were active when the page loaded (the storefront JSON
   * already filters out inactive ones). A customer's cart persists across
   * sessions, so a gift auto-added while a promo was live can still be
   * sitting in their cart days after that promo ended — at which point the
   * Discount Function stops pricing it free, but nothing else in this
   * class ever revisits that line because its promo isn't in `this.promos`
   * any more. Remove any `_free_gift` line whose `_promo` doesn't match a
   * currently-active promo so stale gifts don't silently turn into paid
   * items the customer never chose to buy.
   *
   * A promo staying active doesn't mean its gift list stayed the same — if
   * a promo's gifts are edited in the Toolkit (e.g. swapped from one
   * variant to another) while a customer already has the OLD gift in
   * their cart, that line's `_promo` still matches an active promo, so it
   * would otherwise never be revisited by anything else in this class
   * (`aggregateGiftRequests`/`processCart`'s main loop only ever looks at
   * variants that are CURRENTLY listed as a gift). Also flag lines whose
   * variant isn't among any of its promo's current gift variants.
   */
  findStaleGiftLines (cart) {
    const activeSlugs = new Set(this.promos.map((p) => p.slug))
    const giftVariantsByPromo = new Map(
      this.promos.map((p) => [p.slug, new Set(p.rules.flatMap((r) => r.gifts.map((g) => g.variantId)))]),
    )
    return cart.items.filter((item) => {
      const props = item.properties || {}
      if (props[this.freeGiftPropertyKey] !== 'true') return false
      const promoSlug = props[this.promoPropertyKey]
      if (!promoSlug) return false
      if (!activeSlugs.has(promoSlug)) return true
      const validVariants = giftVariantsByPromo.get(promoSlug)
      return !validVariants || !validVariants.has(item.variant_id)
    })
  }

  processCart (cart, isUpdate, tick = this.nextTick('processCart')) {
    const staleGiftLines = this.findStaleGiftLines(cart)
    if (staleGiftLines.length > 0) {
      this.log(tick, 'processCart: removing stale gift lines (promo no longer active)', {
        staleGiftLines: staleGiftLines.map((l) => this.summariseLine(l)),
      })
      this.removeLines(staleGiftLines.map((l) => l.key), tick)
      return
    }

    const orphanedKeysToRemove = new Set()
    const removesToPerform = new Set()
    const addsToPerform = []
    const updatesToPerform = []
    const manualRemoveFlags = []
    const ruleSnapshots = []

    this.log(tick, 'processCart start', {
      isUpdate,
      isProcessing: this.isProcessing,
      addonsModalActive: this.addonsModalActive,
      cart: this.summariseCart(cart),
    })

    for (const promo of this.promos) {
      const isChoose = promo.selectionMode === 'choose'
      for (const rule of promo.rules) {
        const qualifyingItems = cart.items.filter(
          (item) => item.product_id === rule.qualifyingProductId,
        )
        const qualifyingQty = qualifyingItems.reduce(
          (sum, item) => sum + item.quantity,
          0,
        )
        const ruleKey = this.ruleKey(promo.slug, rule.qualifyingProductId)
        const previousQualifyingQty = this.previousQualifyingQtyByRule.get(ruleKey) || 0
        const qualifyingDecreased = previousQualifyingQty > qualifyingQty

        for (const gift of rule.gifts) {
          if (qualifyingDecreased) {
            const { ghostPaidLines } = this.resolveGiftLines(
              cart,
              promo.slug,
              gift.variantId,
            )
            if (ghostPaidLines.length > 0) {
              ghostPaidLines.forEach((line) => orphanedKeysToRemove.add(line.key))
              ruleSnapshots.push({
                promo: promo.slug,
                giftVariantId: gift.variantId,
                decision: 'cleanup-ghost-paid',
                qualifyingProductId: rule.qualifyingProductId,
                qualifyingDecreased,
                previousQualifyingQty,
                qualifyingQty,
                ghostPaidLines: ghostPaidLines.map((l) => this.summariseLine(l)),
              })
            }
          }
        }

        this.previousQualifyingQtyByRule.set(ruleKey, qualifyingQty)
      }

      const giftRequests = this.aggregateGiftRequests(promo, cart)

      for (const [variantId, request] of giftRequests) {
        const giftKey = this.giftKey(promo.slug, variantId)
        const previousGiftQty = this.previousFreeGiftQtyByGift.get(giftKey) || 0
        const requestedQty = request.requestedQty
        const {
          matchingGiftLines,
          activeGiftLine,
          currentGiftQty,
        } = this.resolveGiftLines(cart, promo.slug, variantId)

        const manuallyRemoved = this.isManuallyRemoved(promo.slug, variantId)
        let decision = 'noop'

        if (requestedQty === 0) {
          this.setManuallyRemoved(promo.slug, variantId, false)
          matchingGiftLines.forEach((line) => removesToPerform.add(line.key))
          decision = matchingGiftLines.length ? 'remove-gift-no-qualifying' : 'noop-no-qualifying'
        } else if (
          !isChoose &&
          isUpdate &&
          previousGiftQty > 0 &&
          matchingGiftLines.length === 0 &&
          requestedQty > 0
        ) {
          manualRemoveFlags.push({ slug: promo.slug, variantId })
          decision = 'flag-manual-remove'
        } else if (!manuallyRemoved) {
          if (matchingGiftLines.length === 0) {
            if (isChoose) {
              decision = 'noop-choose-unselected'
            } else {
              addsToPerform.push({
                variantId,
                quantity: requestedQty,
                slug: promo.slug,
              })
              decision = 'add-gift'
            }
          } else if (currentGiftQty !== requestedQty && activeGiftLine) {
            updatesToPerform.push({
              key: activeGiftLine.key,
              quantity: requestedQty,
            })
            if (matchingGiftLines.length > 1) {
              matchingGiftLines
                .filter((line) => line.key !== activeGiftLine.key)
                .forEach((line) => removesToPerform.add(line.key))
            }
            decision = matchingGiftLines.length > 1 ? 'clamp-qty-and-dedupe' : 'clamp-qty'
          }
        } else {
          decision = 'blocked-manual-remove'
        }

        ruleSnapshots.push({
          promo: promo.slug,
          giftVariantId: variantId,
          decision,
          requestedQty,
          aggregatedRules: request.rules,
          previousGiftQty,
          currentGiftQty,
          manuallyRemoved,
          matchingGiftLines: matchingGiftLines.map((l) => this.summariseLine(l)),
          activeGiftLine: this.summariseLine(activeGiftLine),
        })

        this.previousFreeGiftQtyByGift.set(
          giftKey,
          matchingGiftLines.length === 0 ? 0 : currentGiftQty,
        )
      }

      // Choose-mode backstop: the customer picks exactly one gift, but a
      // returning cart or a mid-swap failure could leave lines for more
      // than one gift variant. Keep the first and remove the rest so the
      // UI (and the Discount Function) only ever see a single chosen gift.
      if (isChoose) {
        const chosenLines = cart.items.filter(
          (item) =>
            item.properties?.[this.freeGiftPropertyKey] === 'true' &&
            item.properties?.[this.promoPropertyKey] === promo.slug,
        )
        const distinctVariants = [...new Set(chosenLines.map((l) => l.variant_id))]
        if (distinctVariants.length > 1) {
          const keepVariant = distinctVariants[0]
          chosenLines
            .filter((l) => l.variant_id !== keepVariant)
            .forEach((l) => removesToPerform.add(l.key))
        }
      }
    }

    manualRemoveFlags.forEach((m) => this.setManuallyRemoved(m.slug, m.variantId, true))

    const removesList = [...removesToPerform]

    const action =
      orphanedKeysToRemove.size > 0
        ? 'remove-orphans'
        : removesList.length > 0
          ? 'remove-lines'
          : updatesToPerform.length > 0
            ? 'update-lines'
            : addsToPerform.length > 0
              ? 'add-gifts'
              : 'none'

    this.log(tick, 'processCart decisions', {
      action,
      ruleSnapshots,
      orphanedKeys: [...orphanedKeysToRemove],
      removesToPerform: removesList,
      updatesToPerform,
      addsToPerform,
      manualRemoveFlags,
    })

    if (orphanedKeysToRemove.size > 0) {
      this.removeLines([...orphanedKeysToRemove], tick)
      return
    }
    if (removesList.length > 0) {
      this.removeLines(removesList, tick)
      return
    }
    if (updatesToPerform.length > 0) {
      this.updateLines(updatesToPerform, tick)
      return
    }
    if (addsToPerform.length > 0) {
      this.addFreeGifts(addsToPerform, tick)
    }
  }

  async addFreeGifts (items, parentTick) {
    if (this.isProcessing || items.length === 0) return
    this.isProcessing = true
    const tick = this.nextTick('addFreeGifts')
    this.log(tick, 'start', { parentTick, items })
    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: item.variantId,
            quantity: item.quantity,
            properties: {
              [this.freeGiftPropertyKey]: 'true',
              [this.promoPropertyKey]: item.slug,
            },
          })),
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      })

      if (!response.ok) throw new Error('Failed to add free gift(s) to cart')
      const data = await this.ensureSections(await response.json())
      this.log(tick, 'cart/add response', {
        ok: response.ok,
        cart: this.summariseCart(data),
        hasSections: Boolean(data.sections && Object.keys(data.sections).length),
      })
      this.renderSections(data, tick)

      items.forEach((item) => {
        this.previousFreeGiftQtyByGift.set(
          this.giftKey(item.slug, item.variantId),
          item.quantity,
        )
      })
    } catch (err) {
      console.error('Free gift: Error adding to cart', err)
      this.warn(tick, 'addFreeGifts failed', err)
    } finally {
      this.finishProcessing('addFreeGifts')
    }
  }

  async updateLines (lines, parentTick) {
    if (this.isProcessing || lines.length === 0) return
    this.isProcessing = true
    const tick = this.nextTick('updateLines')
    this.log(tick, 'start', { parentTick, lines })
    try {
      const updates = {}
      lines.forEach((line) => {
        updates[line.key] = line.quantity
      })
      const response = await fetch(`${window.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      })

      if (!response.ok) throw new Error('Failed to update free gift quantity')
      const data = await this.ensureSections(await response.json())
      this.log(tick, 'cart/update response', {
        ok: response.ok,
        updates,
        cart: this.summariseCart(data),
        hasSections: Boolean(data.sections && Object.keys(data.sections).length),
      })
      this.renderSections(data, tick)
    } catch (err) {
      console.error('Free gift: Error updating quantity', err)
      this.warn(tick, 'updateLines failed', err)
    } finally {
      this.finishProcessing('updateLines')
    }
  }

  async removeLines (keys, parentTick) {
    if (this.isProcessing || keys.length === 0) return
    this.isProcessing = true
    const tick = this.nextTick('removeLines')
    this.log(tick, 'start', { parentTick, keys })
    try {
      const updates = {}
      keys.forEach((key) => {
        updates[key] = 0
      })
      const response = await fetch(`${window.routes.cart_update_url}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        }),
      })

      if (!response.ok) throw new Error('Failed to remove free gift(s)')
      const data = await this.ensureSections(await response.json())
      this.log(tick, 'cart/update (remove) response', {
        ok: response.ok,
        updates,
        cart: this.summariseCart(data),
        hasSections: Boolean(data.sections && Object.keys(data.sections).length),
      })
      this.renderSections(data, tick)
    } catch (err) {
      console.error('Free gift: Error removing line(s)', err)
      this.warn(tick, 'removeLines failed', err)
    } finally {
      this.finishProcessing('removeLines')
    }
  }

  /**
   * Choose-mode: add the customer's selected gift. Quantity mirrors the
   * auto-add clamp (qualifying qty × gift qty) so multi-tent carts get
   * the right number of the chosen gift. Called from
   * `<free-gift-chooser>` via `window.freeGiftHandler`.
   */
  async selectGift (slug, variantId) {
    const promo = this.promos.find((p) => p.slug === slug)
    if (!promo) return
    const numericVariant = FreeGiftHandler.coerceId(variantId)
    if (!numericVariant) return
    let cart
    try {
      const response = await fetch(`${window.routes.cart_url}.js`)
      cart = await response.json()
    } catch (err) {
      console.error('Free gift: Error reading cart for selection', err)
      return
    }
    const requests = this.aggregateGiftRequests(promo, cart)
    const request = requests.get(numericVariant)
    const quantity = request && request.requestedQty > 0 ? request.requestedQty : 1
    this.setManuallyRemoved(slug, numericVariant, false)
    await this.addFreeGifts([{ variantId: numericVariant, quantity, slug }])
  }

  /**
   * Choose-mode: swap the chosen gift for a different one — remove the
   * current gift line(s), then add the newly selected variant.
   */
  async swapGift (slug, fromVariantId, toVariantId) {
    const fromNumeric = FreeGiftHandler.coerceId(fromVariantId)
    const toNumeric = FreeGiftHandler.coerceId(toVariantId)
    if (!toNumeric || fromNumeric === toNumeric) return
    if (fromNumeric) {
      let cart
      try {
        const response = await fetch(`${window.routes.cart_url}.js`)
        cart = await response.json()
      } catch (err) {
        console.error('Free gift: Error reading cart for swap', err)
        return
      }
      const fromLines = cart.items.filter((item) =>
        this.isFreeGiftLine(item, slug, fromNumeric),
      )
      if (fromLines.length > 0) {
        await this.removeLines(fromLines.map((l) => l.key))
      }
    }
    await this.selectGift(slug, toNumeric)
  }

  /**
   * Some Shopify endpoints intermittently return `sections: null`; fall
   * back to a manual fetch so the cart drawer always re-renders.
   */
  async ensureSections (data) {
    if (data.sections && Object.keys(data.sections).length > 0) return data
    try {
      const [drawerRes, bubbleRes] = await Promise.all([
        fetch(`${window.location.pathname}?section_id=cart-drawer`),
        fetch(`${window.location.pathname}?section_id=cart-icon-bubble`),
      ])
      const sections = {}
      if (drawerRes.ok) sections['cart-drawer'] = await drawerRes.text()
      if (bubbleRes.ok) sections['cart-icon-bubble'] = await bubbleRes.text()
      if (Object.keys(sections).length) {
        return { ...data, sections }
      }
    } catch (_e) {}
    return data
  }

  renderSections (data, tick) {
    if (!data || !data.sections) {
      this.warn(tick || 'renderSections', 'no sections in payload', {
        item_count: data?.item_count,
      })
      return
    }
    const cartDrawer = document.querySelector('cart-drawer')
    const willRender = Boolean(cartDrawer && typeof cartDrawer.renderContents === 'function')
    this.log(tick || 'renderSections', {
      willRender,
      sectionKeys: Object.keys(data.sections),
      item_count: data.item_count,
    })
    if (willRender) {
      cartDrawer.renderContents(data)
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.freeGiftHandler = new FreeGiftHandler()
  if (FreeGiftHandler.debugEnabled()) {
    console.log(
      '[free-gift] Debug enabled. Filter console by "[free-gift]". ' +
      'Inspect state: window.freeGiftHandler.debugState(). ' +
      'Disable: localStorage.removeItem("fg_debug") or remove ?fg_debug from URL.',
    )
  }
})
